import React from 'react';
import './OpponentHUD.css';
import { useBattleStatTooltip } from './hooks/useBattleStatTooltip';
import { getTargetingBorderClassName } from './targeting/TargetingBorder';
import { useDelayedBarrierSpriteDisplay } from './hooks/useDelayedBarrierSpriteDisplay';
import { BattlePlayerRankCorner } from './pvp/battlePlayerRankCorner';
import { getFactionBoxBackground, getFactionIconPath } from './utils/factionHudDisplay';
import {
  BATTLE_HEALTH_BARRIER_THRESHOLDS,
  BARRIER_SPRITE_DISMANTLE_DELAY_MS,
  BATTLE_HEALTH_MAX_VISUAL,
} from './constants/battleHealthHud';

/** Native shell coords: must match `.opponent-hud-health-track` (mask slot for health bar). */
const OPPONENT_HUD_HEALTH_BAR_LEFT = 646;
const OPPONENT_HUD_HEALTH_BAR_WIDTH = 304;

/** Same intel chrome as `PlayerHUD.css` (`.player-hud-intel-fill` / `.player-hud-intel-orb`). */
const OPPONENT_HUD_INTEL_BAR_BACKGROUND =
  'linear-gradient(to bottom, #2196f3 0%, #1976d2 50%, #0d47a1 100%)';
const OPPONENT_HUD_INTEL_ORB_BACKGROUND =
  'radial-gradient(circle at 30% 30%, #42a5f5 0%, #1976d2 70%, #0d47a1 100%)';

function getOpponentPrepBonusImageFile(type) {
  if (type === 'plan_ahead') return 'plan_ahead.jpg';
  if (type === 'black_market') return 'black_market_dossier.jpg';
  if (type === 'team_leader') return 'team_leader_briefing.jpg';
  throw new Error(`Unknown opponent preparation bonus type: ${String(type)}`);
}

function getOpponentDiplomaticAccordImageFile(accord) {
  if (accord === 'strategic_coordination') return 'strategic_coordination.jpg';
  if (accord === 'public_relations') return 'public_relations.jpg';
  if (accord === 'emergency_protocol') return 'emergency_protocol.jpg';
  if (accord === 'command_directive') return 'command_directive.jpg';
  throw new Error(`Unknown opponent diplomatic accord type: ${String(accord)}`);
}

const OpponentHUD = ({
  opponentName,
  opponentTitle,
  titleColor,
  health,
  healthDisplayText,
  barriersRemoved = [],
  intel,
  maxIntel = 8,
  profileImage,
  profileImageStyle,
  factionName = null,
  translatedFactionName = null,
  isActiveTurn,
  opponentRank,
  opponentPvpLadderRow,
  getRankStyle,
  showOpponentBanner = true,
  /** Desktop battle only: mirrors former BattleUI drone-zone tiles; anchored above the opponent name strip. */
  showOpponentBonusRow = false,
  showOpponentPreparationBonus = false,
  opponentPreparationBonus = null,
  opponentPreparationBonusUsed = false,
  opponentPreparationBonusTooltip = '',
  opponentDiplomaticAccord = null,
  opponentDiplomaticAccordTooltip = '',
  /** When true (player's turn), opponent strip shows pause; when false (opponent's turn), shows arrows. */
  yourTurn,
  /** Yellow targeting ring when the opponent general is a valid target (strategem, deploy, attack). */
  isGeneralTarget = false,
  /** Desktop BattleUI: attack / deploy / strategem targeting — wired only on the portrait (not the full HUD chrome). */
  onGeneralPortraitClick = null,
  /** When true, show pointer cursor on the general portrait (matches former wrapper hover affordance). */
  generalPortraitPointer = false,
  barrierSpriteDelayMs = BARRIER_SPRITE_DISMANTLE_DELAY_MS,
  barrierVisualResetKey = null,
  /** Mobile landscape: scaled-down shell using the same art/mask as desktop. */
  mobileLayout = false,
}) => {
  const { isBarrierSpriteDismantled } = useDelayedBarrierSpriteDisplay(
    health,
    barriersRemoved,
    barrierSpriteDelayMs,
    barrierVisualResetKey
  );
  const intelOrbLabel = String(intel ?? 0);
  const intelTooltipText = `${intel}/${maxIntel}`;
  const healthDisplay = healthDisplayText || String(health ?? 0);
  const healthPercent = Math.min(100, Math.max(0, ((health ?? 0) / BATTLE_HEALTH_MAX_VISUAL) * 100));
  const factionIconPath = getFactionIconPath(factionName);
  const { showStatTooltip, hideStatTooltip, statTooltipPortal } = useBattleStatTooltip();

  return (
    <div
      className={[
        'opponent-hud-container',
        isActiveTurn ? 'opponent-hud-active-turn' : '',
        showOpponentBanner ? '' : 'opponent-hud-container--no-banner',
        mobileLayout ? 'opponent-hud-container--mobile' : '',
      ].filter(Boolean).join(' ')}
      data-target="opponent-general-area"
      style={{ '--opp-mask-url': 'url(/images/hud_opponent_mask.png)' }}
    >
      <div className="opponent-hud-shell">
        {showOpponentBanner ? (
          <div className="opponent-hud-banner-stack" aria-hidden="true">
            <img
              src="/images/banner_player.png"
              alt=""
              className="opponent-hud-opponent-banner-image"
              draggable={false}
            />
          </div>
        ) : null}

        <div className="opponent-hud-fill opponent-hud-name-bg" />
        <div className="opponent-hud-fill opponent-hud-title-bg" />
        <div className="opponent-hud-fill opponent-hud-health-track">
          <div className="opponent-hud-health-fill" style={{ width: `${healthPercent}%` }} />
        </div>
        {BATTLE_HEALTH_BARRIER_THRESHOLDS.map((threshold) => {
          const positionPx =
            OPPONENT_HUD_HEALTH_BAR_LEFT +
            (threshold / BATTLE_HEALTH_MAX_VISUAL) * OPPONENT_HUD_HEALTH_BAR_WIDTH;
          const destroyed = isBarrierSpriteDismantled(threshold);
          const src = `/images/${destroyed ? 'barrier_destroyed.png' : 'barrier_healthy.png'}`;
          return (
            <img
              key={threshold}
              src={src}
              alt=""
              aria-hidden="true"
              className={`opponent-hud-barrier ${destroyed ? 'opponent-hud-barrier-destroyed' : 'opponent-hud-barrier-healthy'}`}
              style={{ left: `${positionPx}px` }}
              data-tooltip={`Health threshold: ${threshold}`}
              draggable={false}
            />
          );
        })}
        <div
          className="opponent-hud-fill opponent-hud-intel-bg"
          style={{ background: OPPONENT_HUD_INTEL_BAR_BACKGROUND }}
        />
        <div className="opponent-hud-fill opponent-hud-profile-bg" />
        <div
          className="opponent-hud-faction-hit"
          data-tooltip={translatedFactionName || factionName || undefined}
          data-tooltip-position="bottom"
        >
          <div
            className="opponent-hud-fill opponent-hud-faction-bg"
            style={{ background: getFactionBoxBackground(factionName) }}
          >
            {factionIconPath ? (
              <img
                src={factionIconPath}
                alt=""
                className="opponent-hud-faction-icon"
                draggable={false}
                aria-hidden="true"
              />
            ) : null}
          </div>
        </div>

        <div className="opponent-hud-profile-ring">
          <div
            className={`opponent-hud-profile-target ${getTargetingBorderClassName(isGeneralTarget)}`.trim()}
            onClick={
              typeof onGeneralPortraitClick === 'function'
                ? (e) => {
                    e.stopPropagation();
                    onGeneralPortraitClick();
                  }
                : undefined
            }
            style={generalPortraitPointer ? { cursor: 'pointer' } : undefined}
          >
            <img
              src={profileImage}
              alt="Opponent General"
              className="opponent-hud-profile-image"
              style={profileImageStyle}
              data-zone="opponent_general"
              data-target="opponent-general"
            />
          </div>
        </div>

        {/* Small circles beside name / title / health / intel (masked to HUD slots) */}
        <div className="opponent-hud-orb opponent-hud-name-orb" aria-hidden="true">
          <div className="opponent-hud-fill opponent-hud-name-orb-bg" />
          <svg className="opponent-hud-orb-icon opponent-hud-icon-profile" viewBox="0 0 24 24" focusable="false">
            <path
              fill="currentColor"
              d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
            />
          </svg>
        </div>
        <div className="opponent-hud-orb opponent-hud-title-orb" aria-hidden="true">
          <div className="opponent-hud-fill opponent-hud-title-orb-bg" />
          <svg className="opponent-hud-orb-icon opponent-hud-icon-star" viewBox="0 0 24 24" focusable="false">
            <path
              fill="currentColor"
              d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
            />
          </svg>
        </div>
        <div
          className="opponent-hud-orb opponent-hud-health-orb opponent-hud-stat-hit"
          aria-label={`Health: ${healthDisplay}`}
          onMouseEnter={(e) => showStatTooltip(e, healthDisplay)}
          onMouseLeave={hideStatTooltip}
        >
          <div className="opponent-hud-fill opponent-hud-health-orb-bg" />
          <span className="opponent-hud-orb-counter opponent-hud-health-orb-counter">{healthDisplay}</span>
        </div>
        <div
          className="opponent-hud-orb opponent-hud-intel-orb opponent-hud-stat-hit"
          aria-label={`Intelligence: ${intelTooltipText}`}
          onMouseEnter={(e) => showStatTooltip(e, intelTooltipText)}
          onMouseLeave={hideStatTooltip}
        >
          <div
            className="opponent-hud-fill opponent-hud-intel-orb-bg"
            style={{ background: OPPONENT_HUD_INTEL_ORB_BACKGROUND }}
          />
          <span className="opponent-hud-orb-counter opponent-hud-intel-orb-counter">{intelOrbLabel}</span>
        </div>

        {showOpponentBonusRow && (showOpponentPreparationBonus || opponentDiplomaticAccord) ? (
          <div className="opponent-hud-name-accents">
            <div className="opponent-hud-name-accent-slot opponent-hud-name-accent-slot--prep">
              {showOpponentPreparationBonus && opponentPreparationBonus ? (
                <div
                  className={`opponent-hud-bonus-tile ${opponentPreparationBonusUsed ? 'opponent-hud-bonus-tile--used' : ''}`}
                  data-tooltip={opponentPreparationBonusTooltip || undefined}
                  data-tooltip-position="bottom"
                >
                  <img
                    src={`/images/${getOpponentPrepBonusImageFile(opponentPreparationBonus)}`}
                    alt=""
                    draggable={false}
                  />
                </div>
              ) : null}
            </div>
            <div className="opponent-hud-name-accent-slot opponent-hud-name-accent-slot--diplomatic">
              {opponentDiplomaticAccord ? (
                <div
                  className="opponent-hud-bonus-tile opponent-hud-bonus-tile--static"
                  data-tooltip={opponentDiplomaticAccordTooltip || undefined}
                  data-tooltip-position="bottom"
                >
                  <img
                    src={`/images/${getOpponentDiplomaticAccordImageFile(opponentDiplomaticAccord)}`}
                    alt=""
                    draggable={false}
                  />
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="opponent-hud-name">{opponentName || ''}</div>
        <div className="opponent-hud-title" style={{ color: titleColor }}>{opponentTitle || ''}</div>

        <img src="/images/hud_opponent.png" alt="" className="opponent-hud-frame" />

        {/* Same layering as PlayerHUD: ribbon behind frame (z0); rank anchor sibling above frame (z3). */}
        {showOpponentBanner ? (
          <div className="opponent-hud-banner-rank-anchor">
            <BattlePlayerRankCorner
              playerRank={opponentRank}
              playerPvpLadderRow={opponentPvpLadderRow}
              getRankStyle={getRankStyle}
            />
          </div>
        ) : null}

        {/* Above the PNG frame so the chip is not covered by opaque frame art; beside intel strip (native coords). */}
        <div className="opponent-hud-turn-layer">
          {/* Opponent POV: arrows when it is their turn (yourTurn false); pause when they are idle (your turn). */}
          {yourTurn ? (
            <div className="opponent-hud-paused" aria-hidden="true">
              <span className="opponent-hud-paused-bar" />
              <span className="opponent-hud-paused-bar" />
            </div>
          ) : (
            <div className="opponent-hud-turn-arrows" aria-hidden="true">
              <span className="opponent-hud-turn-arrow" style={{ animationDelay: '0s' }} />
              <span className="opponent-hud-turn-arrow" style={{ animationDelay: '0.2s' }} />
              <span className="opponent-hud-turn-arrow" style={{ animationDelay: '0.4s' }} />
            </div>
          )}
        </div>
      </div>
      {statTooltipPortal}
    </div>
  );
};

export default OpponentHUD;
